import { SSCarouselResult } from "../search-spring/types/types"
import { type_GM_STATE } from './types'
import generateUUID from "./generateUUID"

declare var GM_STATE: type_GM_STATE
const { searchspring } = GM_STATE.integrations
const ssPageLoadId = sessionStorage.getItem('ss-page-load-id')
// Events on the same page always need the same pageLoadId so we will set this here so every event has the same id
const pageLoadId = ssPageLoadId
// set the Id here so child events can use this as the parentId
const parentLevelId = ssPageLoadId
// We need this id to be usable in the profile.click event then it needs to be saved so it can
// be passed as the pid of the child event profile.product.click

const intellisuggestTrackClick = (elementHref: string, data: string, signature: string): boolean => {
  const escapeFn = encodeURIComponent || escape
  let apiUrl = ''

  if (document.images) {
    if (location.protocol === 'https:') {
      apiUrl = `https://${searchspring.siteId}.a.searchspring.io/api/`
    } else {
      apiUrl = `http://${searchspring.siteId}.a.searchspring.io/api/`
    }

    const imgTag = new Image()

    imgTag.src =
      apiUrl +
      'track/track.json?d=' +
      data +
      '&s=' +
      signature +
      '&u=' +
      escapeFn(elementHref) +
      '&r=' +
      escapeFn(document.referrer)
  }

  return true
}

const generateSeed = (product: SSCarouselResult) => {
  return { sku: product.mappings.core.sku }
}

const generatePlacementPosition = (tag: string) => {
  if (!tag) return
  tag.toLowerCase()

  switch (tag) {
    case '404':
      return '404 Page'
    case 'cross-sell':
      return 'Product Detail Page'
    case 'home':
      return 'Home Page'
    case 'no-results':
      return 'No Results'
    case 'recently-viewed':
      return 'Other Page'
    case 'similar':
      return 'Product Detail Page'
    case 'view-cart':
      return 'Basket Page'
    default:
      return 'Home Page'
  }
}

const getSsSessionIDFromCookie = () => {
  const splitString = document.cookie.split('ssSessionIdNamespace=')

  if (splitString.length > 1) {
    const valueAndRemaining = splitString[1].split(';')
    const ssSessionIdNamespaceValue = valueAndRemaining[0]

    return ssSessionIdNamespaceValue
  } else {
    // eslint-disable-next-line no-console
    console.warn('ssSessionIdNamespace not found in the string.')

    return null
  }
}

const getSsUserIdFromCookie = () => {
  const splitString = document.cookie.split('ssUserId=')

  if (splitString.length > 1) {
    const valueAndRemaining = splitString[1].split(';')
    const ssUserIdValue = valueAndRemaining[0]

    return ssUserIdValue
  } else {
    // eslint-disable-next-line no-console
    console.warn('ssUserId not found in the string.')

    return null
  }
}

const generateBeaconContext = () => {
  return {
    website: {
      trackingCode: searchspring.siteId
    },
    userId: getSsUserIdFromCookie(),
    sessionId: getSsSessionIDFromCookie(),
    pageLoadId
  }
}

const generateBeaconProductData = (product: SSCarouselResult) => {
  return {
    id: product.id,
    seed: generateSeed(product), // This needs to be the same seed as the initial sku that was clicked
    mappings: {
      core: {
        uid: product.mappings.core.uid,
        name: product.mappings.core.name,
        sku: product.mappings.core.sku,
        price: product.mappings.core.price,
        url: product.mappings.core.url,
        brand: product.mappings.core.brand
      }
    }
  }
}

const generateBeacon = (sku: string, tag: string, type: string) => {
  const core = {
    category: 'searchspring.recommendations.user-interactions',
    id: parentLevelId,
    type,
    context: generateBeaconContext(),
    event: {
      context: {
        type: 'product-recommendation',
        tag,
        placement: generatePlacementPosition(tag)
      },
      profile: {
        tag,
        placement: generatePlacementPosition(tag),
        seed: [sku ? {sku: sku} : '']
      }
    }
  }

  return core
}

const generateBeaconProduct = (product: SSCarouselResult, tag: string, type: string, pid: string | null) => {
  const data = {
    type,
    category: 'searchspring.recommendations.user-interactions',
    id: generateUUID(),
    pid: pid,
    event: {
      product: generateBeaconProductData(product),
      context: {
        type: 'product-recommendation',
        tag,
        placement: generatePlacementPosition(tag)
      }
    },
    context: generateBeaconContext()
  }

  return data
}

const generateBeaconProducts = (products: SSCarouselResult[], tag: string, type: string, pid: string | null) => {
  if (!pid) {
    pid = parentLevelId
  }

  return products.map((product) => generateBeaconProduct(product, tag, type, pid))
}

const generateBeaconClick = (product: SSCarouselResult, tag: string, type: string) => {
  const data = {
    type: type,
    category: 'searchspring.recommendations.user-interactions',
    id: parentLevelId,
    context: generateBeaconContext(),
    event: {
      profile: {
        tag: tag,
        placement: generatePlacementPosition(tag),
        seed: generateSeed(product)
      },
      context: {
        type: 'product-recommendation',
        tag: tag,
        placement: generatePlacementPosition(tag)
      }
    }
  }

  return data
}

const generateBeaconProductClick = (product: SSCarouselResult, tag: string, type: string, pid: string | null) => {
  if (!pid) {
    pid = parentLevelId
  }

  const data = {
    type: type,
    category: 'searchspring.recommendations.user-interactions',
    id: generateUUID(),
    pid: pid,
    context: generateBeaconContext(),
    event: {
      context: {
        type: 'product-recommendation',
        tag: tag,
        placement: generatePlacementPosition(tag)
      },
      product: generateBeaconProductData(product)
    }
  }

  return data
}

const postBeaconTracking = async (data: any) => {
  const config = {
    method: 'POST',
    redirect: 'follow',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  }

  return fetch('https://beacon.searchspring.io/beacon', config).then((result) => result)
}

const redirect = (handle: string, ctrlKey = false) => {
  const newUrl = `${window.location.origin}${handle}`

  if (ctrlKey) {
    window.open(newUrl, '_blank')?.focus()

    return
  }

  window.location.href = newUrl
}

export {
  generateBeaconProducts,
  generateBeacon,
  generateBeaconClick,
  generateBeaconProductClick,
  postBeaconTracking,
  intellisuggestTrackClick,
  redirect
}
